import { createTheme, useTheme } from "@mui/material/styles";

// shadcn-inspired neutral (slate) design tokens.
// Shared so component-level sx can reference the same values as the theme,
// via `useTokens()` (or `useTheme().tokens`) so colors respond to light/dark
// mode switching (see ThemeModeProvider).
//
// Every foreground/background pair below is checked against WCAG 2.2 AA:
// 4.5:1 for text, 3:1 for icons, focus rings and component boundaries.

// Tokens shared between light and dark mode. The nav rail sits outside the
// light/dark toggle — it stays a fixed dark surface in both modes.
const SHARED = {
  sidebar: "#0f172a", // slate-900 (dark neutral rail)
  sidebarHover: "#1e293b", // slate-800
  sidebarActive: "#334155", // slate-700
  sidebarForeground: "#cbd5e1", // slate-300 (11.9:1 on the rail)
  sidebarForegroundActive: "#ffffff",
  sidebarDestructive: "#f87171", // red-400 (6.5:1 on the rail)
  sidebarDestructiveHover: "#fca5a5", // red-300
  radius: 8,
  radiusLg: 12,
};

export const LIGHT_TOKENS = {
  ...SHARED,
  // Surfaces
  appBg: "#f8fafc", // slate-50  (page canvas)
  card: "#ffffff", // white      (cards / tables / panels)
  muted: "#f1f5f9", // slate-100 (hover / subtle fills / table head)

  // Text
  foreground: "#0f172a", // slate-900
  // slate-600 (bumped from slate-500 #64748b, which fell to 4.34:1 against
  // the slate-100 table-head fill below the 4.5:1 WCAG 2.2 text minimum)
  mutedForeground: "#475569",

  // Lines / accents
  border: "#e2e8f0", // slate-200
  borderStrong: "#cbd5e1", // slate-300
  // Input outlines need 3:1 against the surface to read as a control
  // (WCAG 1.4.11). slate-500 is 4.8:1 on white.
  input: "#64748b",
  // Keyboard focus ring. slate-500 is 4.8:1 on white and 3.7:1 on the rail.
  ring: "#64748b",
  primary: "#0f172a", // slate-900 (near-black primary)
  primaryHover: "#1e293b", // slate-800
  primaryForeground: "#f8fafc",
  destructive: "#dc2626", // red-600 (4.8:1 on white)
  // Fill for destructive buttons/headers that carry white text.
  destructiveStrong: "#dc2626", // red-600 (white text 4.8:1)
  destructiveStrongHover: "#b91c1c", // red-700
  // Text-safe error/validation color, distinct from tokens.destructive so
  // literal red form-error text stays above 4.5:1 in both modes.
  errorText: "#b91c1c", // red-700

  // Status callouts (shadcn Alert style): tinted surface, 1px border,
  // dark text, saturated icon.
  info: { bg: "#eff6ff", border: "#bfdbfe", fg: "#1e3a8a", icon: "#2563eb" },
  success: { bg: "#f0fdf4", border: "#bbf7d0", fg: "#14532d", icon: "#15803d" },
  warning: { bg: "#fffbeb", border: "#fde68a", fg: "#78350f", icon: "#b45309" },
  error: { bg: "#fef2f2", border: "#fecaca", fg: "#7f1d1d", icon: "#dc2626" },

  // Elevation (shadcn shadow-sm / shadow-lg).
  shadowSm: "0 1px 2px 0 rgb(15 23 42 / 0.05)",
  shadowLg:
    "0 10px 15px -3px rgb(15 23 42 / 0.10), 0 4px 6px -4px rgb(15 23 42 / 0.10)",
};

export const DARK_TOKENS = {
  ...SHARED,
  // Surfaces
  appBg: "#020617", // slate-950 (page canvas)
  card: "#0f172a", // slate-900 (cards / tables / panels)
  muted: "#1e293b", // slate-800 (hover / subtle fills / table head)

  // Text
  foreground: "#f8fafc", // slate-50
  mutedForeground: "#94a3b8", // slate-400 (5.7:1 on slate-800)

  // Lines / accents
  border: "#1e293b", // slate-800
  borderStrong: "#334155", // slate-700
  input: "#64748b", // slate-500 (3.75:1 on slate-900)
  ring: "#cbd5e1", // slate-300
  primary: "#f8fafc", // slate-50 (near-white primary, inverted for contrast)
  primaryHover: "#e2e8f0", // slate-200
  primaryForeground: "#0f172a",
  // red-400: used as an icon/checkbox/text accent on dark surfaces, where
  // red-500 only reached 4.1:1.
  destructive: "#f87171",
  // tokens.destructive + white text would fail, so filled destructive
  // buttons and headers use this darker red instead (white text 6.5:1).
  destructiveStrong: "#b91c1c", // red-700
  destructiveStrongHover: "#991b1b", // red-800
  // red-300: tokens.errorText needs to read on dark surfaces, not just pass
  // as a button/icon accent.
  errorText: "#fca5a5",

  info: { bg: "#172554", border: "#1e3a8a", fg: "#dbeafe", icon: "#60a5fa" },
  success: { bg: "#052e16", border: "#14532d", fg: "#dcfce7", icon: "#4ade80" },
  warning: { bg: "#451a03", border: "#78350f", fg: "#fef3c7", icon: "#fbbf24" },
  error: { bg: "#450a0a", border: "#7f1d1d", fg: "#fee2e2", icon: "#f87171" },

  shadowSm: "0 1px 2px 0 rgb(0 0 0 / 0.4)",
  shadowLg: "0 10px 15px -3px rgb(0 0 0 / 0.5), 0 4px 6px -4px rgb(0 0 0 / 0.5)",
};

// Backwards-compatible default export of the light tokens.
export const SHADCN = LIGHT_TOKENS;

const TOKENS_BY_MODE = {
  light: LIGHT_TOKENS,
  dark: DARK_TOKENS,
};

export function getTokens(mode) {
  return TOKENS_BY_MODE[mode] || LIGHT_TOKENS;
}

// Resolves the token bag for any MUI theme. Falls back to the tokens for the
// theme's palette mode when a component renders under a theme that was not
// built by createAppTheme (e.g. MUI's default theme in an isolated test), so
// a missing provider degrades to the base palette instead of crashing.
export function getThemeTokens(theme) {
  return theme?.tokens || getTokens(theme?.palette?.mode);
}

export function useTokens() {
  return getThemeTokens(useTheme());
}

export const FONT_FAMILY = [
  '"Inter"',
  "ui-sans-serif",
  "system-ui",
  "-apple-system",
  '"Segoe UI"',
  "Roboto",
  '"Helvetica Neue"',
  "Arial",
  "sans-serif",
].join(", ");

export function createAppTheme(mode = "light") {
  const tokens = getTokens(mode);

  // MUI's default disabled-state opacities (26% / 12% overlays) are tuned for
  // generic backgrounds and render disabled buttons/inputs as nearly
  // invisible against this app's surfaces. Boost disabled-state contrast in
  // both modes so the control stays legible while still reading as disabled.
  const actionOverrides =
    mode === "dark"
      ? {
          disabled: "rgba(148, 163, 184, 0.75)", // slate-400
          disabledBackground: "rgba(148, 163, 184, 0.24)",
          disabledOpacity: 0.55,
        }
      : {
          disabled: "rgba(71, 85, 105, 0.7)", // slate-600
          disabledBackground: "rgba(100, 116, 139, 0.22)", // slate-500
          disabledOpacity: 0.6,
        };

  const disabledButton =
    mode === "dark"
      ? {
          color: "rgba(203, 213, 225, 0.85)", // slate-300
          contained: "rgba(148, 163, 184, 0.28)", // slate-400 fill
          border: "rgba(148, 163, 184, 0.4)",
        }
      : {
          color: "rgba(51, 65, 85, 0.75)", // slate-700
          contained: "rgba(100, 116, 139, 0.24)", // slate-500 fill
          border: "rgba(100, 116, 139, 0.5)",
        };

  const focusRing = {
    outline: `2px solid ${tokens.ring}`,
    outlineOffset: "2px",
  };

  const alertStyle = (severity) => ({
    color: tokens[severity].fg,
    backgroundColor: tokens[severity].bg,
    border: `1px solid ${tokens[severity].border}`,
    "& .MuiAlert-icon": { color: tokens[severity].icon, opacity: 1 },
  });

  const theme = createTheme({
    shape: {
      borderRadius: tokens.radius,
    },
    palette: {
      mode,
      primary: {
        main: tokens.primary,
        dark: tokens.primaryHover,
        contrastText: tokens.primaryForeground,
      },
      secondary: {
        main: mode === "dark" ? "#94a3b8" : "#475569", // slate-400 / slate-600
        contrastText: mode === "dark" ? "#0f172a" : "#f8fafc",
      },
      error: {
        // Used by MUI for helper text, labels and invalid outlines, so it
        // must clear 4.5:1 against the card surface in each mode.
        main: mode === "dark" ? tokens.destructive : tokens.destructiveStrong,
        contrastText: mode === "dark" ? "#0f172a" : "#ffffff",
      },
      warning: { main: tokens.warning.icon },
      info: { main: tokens.info.icon },
      success: { main: tokens.success.icon },
      action: actionOverrides,
      background: {
        default: tokens.appBg,
        paper: tokens.card,
      },
      text: {
        primary: tokens.foreground,
        secondary: tokens.mutedForeground,
      },
      divider: tokens.border,
    },
    typography: {
      fontFamily: FONT_FAMILY,
      allVariants: {
        fontFamily: FONT_FAMILY,
        textTransform: "none",
      },
      // 14px base (shadcn text-sm) instead of 13px for legibility.
      fontSize: 14,
      h1: { fontWeight: 700, letterSpacing: "-0.025em" },
      h2: { fontWeight: 700, letterSpacing: "-0.025em" },
      h3: { fontWeight: 600, letterSpacing: "-0.02em" },
      h4: { fontWeight: 600, letterSpacing: "-0.02em" },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: {
        textTransform: "none",
        fontWeight: 600,
        letterSpacing: 0,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: tokens.appBg, color: tokens.foreground },
        },
      },
      // Visible keyboard focus on every clickable MUI element (buttons, icon
      // buttons, list items, tabs, checkboxes, menu items).
      MuiButtonBase: {
        defaultProps: {
          disableRipple: false,
        },
        styleOverrides: {
          root: {
            "&.Mui-focusVisible": focusRing,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: tokens.radius,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            transition:
              "background-color 150ms ease, border-color 150ms ease, color 150ms ease",
            "&.Mui-disabled": {
              color: disabledButton.color,
            },
          },
          sizeLarge: {
            fontSize: "0.875rem",
            padding: "8px 18px",
            minHeight: 40,
          },
          sizeMedium: {
            fontSize: "0.875rem",
            minHeight: 36,
          },
          containedPrimary: {
            "&:hover": { backgroundColor: tokens.primaryHover },
          },
          containedError: {
            backgroundColor: tokens.destructiveStrong,
            color: "#ffffff",
            "&:hover": { backgroundColor: tokens.destructiveStrongHover },
          },
          contained: {
            "&.Mui-disabled": {
              color: disabledButton.color,
              backgroundColor: disabledButton.contained,
            },
          },
          // shadcn "outline" button: neutral border, card background, muted
          // fill on hover. Used for Cancel / secondary dialog actions.
          outlined: {
            backgroundColor: tokens.card,
            borderColor: tokens.borderStrong,
            "&:hover": {
              backgroundColor: tokens.muted,
              borderColor: tokens.borderStrong,
            },
            "&.Mui-disabled": {
              color: disabledButton.color,
              borderColor: disabledButton.border,
            },
          },
          outlinedPrimary: {
            color: tokens.foreground,
          },
          text: {
            "&:hover": { backgroundColor: tokens.muted },
          },
          textPrimary: {
            color: tokens.foreground,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius,
          },
        },
      },
      MuiCheckbox: {
        defaultProps: {
          color: "primary",
        },
        styleOverrides: {
          root: {
            color: tokens.input,
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: tokens.input,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 500,
            boxShadow: "none",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
          elevation1: { boxShadow: tokens.shadowSm },
          elevation2: { boxShadow: tokens.shadowSm },
          outlined: { borderColor: tokens.border },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radiusLg,
            border: `1px solid ${tokens.border}`,
            boxShadow: tokens.shadowSm,
          },
        },
      },
      // shadcn Dialog: bordered card, large radius, soft shadow, plain title
      // row separated from the body and footer by hairlines.
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: tokens.radiusLg,
            border: `1px solid ${tokens.border}`,
            boxShadow: tokens.shadowLg,
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            "&:not(.MuiBackdrop-invisible)": {
              backgroundColor: "rgb(2 6 23 / 0.6)", // slate-950 / 60%
            },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: "1.125rem",
            fontWeight: 600,
            lineHeight: 1.4,
            letterSpacing: "-0.01em",
            color: tokens.foreground,
            backgroundColor: tokens.card,
            padding: "18px 24px",
            borderBottom: `1px solid ${tokens.border}`,
            // MUI zeroes the top padding of content that directly follows a
            // title; restore breathing room now the title has a divider.
            "& + .MuiDialogContent-root.MuiDialogContent-root": {
              paddingTop: "20px",
            },
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: "16px 24px",
            gap: "8px",
            borderTop: `1px solid ${tokens.border}`,
            flexWrap: "wrap",
            "& > :not(style) ~ :not(style)": { marginLeft: 0 },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius,
            fontSize: "0.875rem",
            lineHeight: 1.5,
            alignItems: "flex-start",
          },
          standardInfo: alertStyle("info"),
          outlinedInfo: alertStyle("info"),
          standardSuccess: alertStyle("success"),
          outlinedSuccess: alertStyle("success"),
          standardWarning: alertStyle("warning"),
          outlinedWarning: alertStyle("warning"),
          standardError: alertStyle("error"),
          outlinedError: alertStyle("error"),
          message: { fontWeight: 500 },
        },
      },
      MuiAlertTitle: {
        styleOverrides: {
          root: { fontWeight: 600, marginBottom: 2, color: "inherit" },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius,
            backgroundColor: tokens.card,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: tokens.input,
            },
            "&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline":
              {
                borderColor: tokens.foreground,
              },
            "&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
              borderColor: tokens.ring,
              borderWidth: 2,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: tokens.mutedForeground,
            "&.Mui-focused:not(.Mui-error)": { color: tokens.foreground },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            "&.Mui-error": { color: tokens.errorText },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: tokens.border,
          },
          head: {
            color: tokens.mutedForeground,
            backgroundColor: tokens.muted,
            fontWeight: 600,
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            boxShadow: "none",
            border: `1px solid ${tokens.border}`,
            borderRadius: tokens.radius,
            overflow: "hidden",
            "&:before": { display: "none" },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.muted,
            color: tokens.foreground,
            fontWeight: 600,
            "& .MuiAccordionSummary-expandIconWrapper": {
              color: tokens.mutedForeground,
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            border: `1px solid ${tokens.border}`,
            boxShadow: tokens.shadowLg,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            border: `1px solid ${tokens.border}`,
            boxShadow: tokens.shadowLg,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: tokens.foreground,
            color: tokens.primaryForeground,
            fontSize: "12px",
            fontWeight: 500,
            borderRadius: 6,
          },
          arrow: {
            color: tokens.foreground,
          },
        },
      },
    },
  });

  // Custom token bag, kept in sync with the active mode so components can do
  // `const tokens = useTokens();` and get live-updating colors.
  theme.tokens = tokens;

  return theme;
}

const theme = createAppTheme("light");

export default theme;
